  import { Suspense, useState, useEffect, useRef } from "react";
  import { DataTable } from "primereact/datatable";
  import { Column } from "primereact/column";
  import { ColumnGroup } from "primereact/columngroup";
  import { Row } from "primereact/row";
  import { Toast } from "primereact/toast";
  import { Card } from "primereact/card";
  import { Calendar } from "primereact/calendar";
  import { Dialog } from "primereact/dialog";
  import { Dropdown } from "primereact/dropdown";
  import { Tag } from "primereact/tag";
  import { MultiSelect } from "primereact/multiselect";
  import { Button } from "primereact/button";
  import AppLoading from "../app-loading/app-loading";
  import UnassignedCustomerPopup from "./CustomerExecutive-unassignedmapping";
  import { LastOrderResponse } from "../../services/edit-users/edit-users.service";

  import {
    CEPerformanceReport1,
    CEPerformanceReportResponseItem,
    UnassignedCustomerReport,
    getCardForCEReport,
    CardForCEReportResponse,
    getCECallStatusPivot,
    CECallStatusPivotRequest,
    CECallStatusPivotResponseItem,
    getCustomerCallStatusSummary,
    CustomerCallStatusResponseItem,
    getLastFiveOrdersfromuserid
  } from "../../services/edit-users/edit-users.service";
  import ReportPageHeader from "../shared/ReportPageHeader";

  interface CustomerCallStatusResponseItemWithDynamic extends CustomerCallStatusResponseItem {
    dynamicColumns?: Record<string, string>;
  }

  interface CECallStatusPivotResponseItemWithTotal extends CECallStatusPivotResponseItem {
    rowTotal?: number;
  }

  const CECardsMonthYearDropdown: React.FC = () => {
    const toast = useRef<Toast>(null);
    const today = new Date();
    const [loading, setLoading] = useState(false);
    const [loadingPivot, setLoadingPivot] = useState(false);
    const [datatableData, setDatatableData] = useState<CEPerformanceReportResponseItem[]>([]);
    const [unassignedData, setUnassignedData] = useState<UnassignedCustomerReport | null>(null);
    const [cardData, setCardData] = useState<CardForCEReportResponse | null>(null);
    const [pivotData, setPivotData] = useState<CECallStatusPivotResponseItemWithTotal[]>([]);
    const [popupVisible, setPopupVisible] = useState(false);

    // Pivot popup state
    const [pivotPopupVisible, setPivotPopupVisible] = useState(false);
    const [pivotPopupData, setPivotPopupData] = useState<CustomerCallStatusResponseItemWithDynamic[]>([]);
    const [pivotTotalRecords, setPivotTotalRecords] = useState(0);
    const [loadingPivotPopup, setLoadingPivotPopup] = useState(false);
    const [pivotPageNumber, setPivotPageNumber] = useState(1);
    const [pivotPageSize, setPivotPageSize] = useState(10);
    const [pivotDynamicColumns, setPivotDynamicColumns] = useState<string[]>([]);
    const [selectedPivotStatus, setSelectedPivotStatus] = useState<{ callStatusId: number; callStatusName: string } | null>(null);
    const [selectedPivotExecutive, setSelectedPivotExecutive] = useState<{ salesExecutiveId: number; salesExecutiveName: string } | null>(null);
    const [pivotSortField, setPivotSortField] = useState<string>("customerName");
    const [pivotSortOrder, setPivotSortOrder] = useState<1 | -1 | 0>(1);

    // Column selection for pivot popup
    const pivotBaseColumns = [
     { field: "customerName", header: "Customer Name" },
    { field: "contactNumber", header: "Contact Number" },
    { field: "orderID", header: "Last Ordered ID" },
    { field: "orderDate", header: " Last Ordered Date" },
    { field: "taxableAmount", header: "Last Ordered value" },
    { field: "lastCalledDate", header: "Last Called Date" },
    ];
    
    const [pivotSelectedColumns, setPivotSelectedColumns] = useState<any[]>(pivotBaseColumns);
    const [pivotAllAvailableColumns, setPivotAllAvailableColumns] = useState<any[]>(pivotBaseColumns);

    // Last Orders Popup
    const [lastOrdersPopupVisible, setLastOrdersPopupVisible] = useState(false);
    const [lastOrdersData, setLastOrdersData] = useState<LastOrderResponse[]>([]);
    const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
    const [loadingLastOrders, setLoadingLastOrders] = useState(false);

    type ExtendedCEPerformanceReportResponseItem = CEPerformanceReportResponseItem & {
      isUnassigned?: boolean;
      isTotal?: boolean;
    };

    const getCurrentMonth = () => {
      const year = today.getFullYear();
      const month = (today.getMonth() + 1).toString().padStart(2, "0");
      return `${year}-${month}`;
    };
    const [monthYear, setMonthYear] = useState(getCurrentMonth());

    const handleRefresh = () => {
      fetchData();
      fetchPivotData();
    };

    const handleResetFilters = () => {
      const nextMonth = getCurrentMonth();
      setMonthYear(nextMonth);
      if (nextMonth === monthYear) {
        fetchData();
        fetchPivotData();
      }
    };

    useEffect(() => {
      fetchData();
      fetchPivotData();
    }, [monthYear]);

    const fetchData = async () => {
      if (!monthYear) return;
      setLoading(true);
      try {
        const [year, month] = monthYear.split("-").map(Number);
        const requestPayload = { salesMonth: month, salesYear: year };

        const [tableResponse, cardResponse] = await Promise.all([
          CEPerformanceReport1(requestPayload),
          getCardForCEReport(requestPayload),
        ]);

        const normalizedUnassigned: UnassignedCustomerReport = {
          uC_InvoicedForTheDay: tableResponse.unassignedCustomers.uC_InvoicedForTheDay ?? 0,
          uC_InvoicedTillDate: tableResponse.unassignedCustomers.uC_InvoicedTillDate ?? 0,
          uC_OpenOrderForPeriod: tableResponse.unassignedCustomers.uC_OpenOrderForPeriod ?? 0,
        };

        setDatatableData(tableResponse.executives);
        setUnassignedData(normalizedUnassigned);
        setCardData(cardResponse);
      } catch (err) {
        console.error(err);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to fetch data",
          life: 3000,
        });
      } finally {
        setLoading(false);
      }
    };

    const fetchPivotData = async () => {
      if (!monthYear) return;
      setLoadingPivot(true);
      try {
        const [year, month] = monthYear.split("-").map(Number);
        const requestPayload: CECallStatusPivotRequest = { month, year };
        const response = await getCECallStatusPivot(requestPayload);

        // Add rowTotal to each pivot data item
        const pivotDataWithTotals = response.map(row => ({
          ...row,
          rowTotal: Object.values(row.ceCounts).reduce((sum: number, count) => sum + (count ?? 0), 0)
        }));

        setPivotData(pivotDataWithTotals);
      } catch (err) {
        console.error(err);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to fetch CE pivot data",
          life: 3000,
        });
      } finally {
        setLoadingPivot(false);
      }
    };

    // ✅ formatters
    const formatRupees = (amount: number) =>
      new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(Math.round(amount ?? 0));

    const formatNumber = (num: number) => Math.round(num ?? 0);

    const formatDate = (date: Date) => {
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    const formatDateTime = (dateString: string | null) => {
      if (!dateString) return "--";
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" });
      } catch {
        return dateString;
      }
    };

    const formatTaxableAmount = (amount: string | null | undefined) => {
      if (!amount) return "--";
      try {
        const numAmount = Math.round(parseFloat(amount));
        return formatRupees(numAmount);
      } catch {
        return amount;
      }
    };

    const getStatusSeverity = (status: string) => {
      if (!status) return null;
      const statusLower = status.toLowerCase();
      if (statusLower.includes("order received")) return "success";
      if (statusLower.includes("call not received")) return "warning";
      if (statusLower.includes("not required")) return "info";
      if (statusLower.includes("will order shortly")) return "success";
      if (statusLower.includes("phone not reachable")) return "danger";
      if (statusLower.includes("call me later")) return "warning";
      return null;
    };

    const statusBodyTemplate = (value: string) => {
      const severity = getStatusSeverity(value);
      return severity ? <Tag value={value} severity={severity} /> : value;
    };

    // Custom body templates for pivot popup columns
    const pivotColumnBodyTemplate = (rowData: any, column: any) => {
    const value = rowData[column.field];

    if (column.field === "customerName") {
      return (
        <span
          className="text-blue-600 cursor-pointer underline"
          onClick={() => fetchLastFiveOrdersByUser(rowData.customerId)}
          title="Click to view last 5 orders"
        >
          {value || "--"}
        </span>
      );
    }

    switch (column.field) {
      case "orderDate":
        return formatDateTime(value);
      case "taxableAmount":
        return formatTaxableAmount(value);
      case "lastCalledDate":
        return formatDateTime(value);
      default:
        if (pivotDynamicColumns.includes(column.field)) {
          return statusBodyTemplate(rowData.dynamicColumns?.[column.field] || "--");
        }
        return value || "--";
    }
  };

    const fetchLastFiveOrdersByUser = async (userId: number) => {
  if (!userId) return;

  setLoadingLastOrders(true);
  setSelectedCustomerId(userId);

  try {
    const orders = await getLastFiveOrdersfromuserid(userId);
    setLastOrdersData(orders);
    setLastOrdersPopupVisible(true);
  } catch (err) {
    console.error("Failed to fetch last 5 orders", err);
    toast.current?.show({
      severity: "error",
      summary: "Error",
      detail: "Failed to fetch last 5 orders",
      life: 3000,
    });
  } finally {
    setLoadingLastOrders(false);
  }
};

    const headerGroup = (
      <ColumnGroup>
        <Row>
          <Column header="Customer Service Executive" rowSpan={2} />
          <Column
            header={<div style={{ marginLeft: "290px" }}>Values</div>}
            colSpan={6}
            className="font-semibold "
          />
          <Column
            header={<div style={{ marginLeft: "100px" }}>Call Count</div>}
            colSpan={3}
            style={{ textAlign: "center" }}
        
          />
        </Row>
        <Row>
          <Column header="Target" />
          <Column header={`Invoices (${formatDate(today)})`} />
          <Column header="Cumulative Monthly Invoices" />
          <Column header="Balance Target" />
          <Column header="Target Achieved (%)" />
          <Column header={`Open Order (${formatDate(today)})`} />
          <Column header={`Calls (${formatDate(today)})`} />
          <Column header="Cumulative Monthly Calls" />
          <Column header="Total Assigned" />
        </Row>
      </ColumnGroup>
    );

    const ceColumns = Array.from(new Set(pivotData.flatMap((item) => Object.keys(item.ceCounts))));

    const totals: { [ce: string]: number } = {};
    ceColumns.forEach((ce) => {
      totals[ce] = pivotData.reduce((sum, row) => sum + (row.ceCounts[ce] ?? 0), 0);
    });

    const unassignedRow: ExtendedCEPerformanceReportResponseItem | null = unassignedData
      ? {
          salesExecutiveID: 0,
          reportDate: "",
          monthStartDate: "",
          customerServiceExecutive: "Unassigned Customers",
          targetForTheMonth: 0,
          invoicedForTheDay: unassignedData.uC_InvoicedForTheDay,
          invoicedTillDate: unassignedData.uC_InvoicedTillDate,
          balanceTargetToAchieve: 0,
          achievedPercent: 0,
          openOrderAsOnDate: unassignedData.uC_OpenOrderForPeriod,
          callCountForTheDay: 0,
          callCountMonthTillDate: 0,
          totalAssignedCustomers: 0,
          isUnassigned: true,
        }
      : null;

    const totalRow: ExtendedCEPerformanceReportResponseItem = {
      salesExecutiveID: -1,
      reportDate: "",
      monthStartDate: "",
      customerServiceExecutive: "Total",
      targetForTheMonth: datatableData.reduce((sum, r) => sum + (r.targetForTheMonth ?? 0), 0),
      invoicedForTheDay:
        datatableData.reduce((sum, r) => sum + (r.invoicedForTheDay ?? 0), 0) +
        (unassignedData?.uC_InvoicedForTheDay ?? 0),
      invoicedTillDate:
        datatableData.reduce((sum, r) => sum + (r.invoicedTillDate ?? 0), 0) +
        (unassignedData?.uC_InvoicedTillDate ?? 0),
      balanceTargetToAchieve: datatableData.reduce((sum, r) => sum + (r.balanceTargetToAchieve ?? 0), 0),
      achievedPercent: 0,
      openOrderAsOnDate:
        datatableData.reduce((sum, r) => sum + (r.openOrderAsOnDate ?? 0), 0) +
        (unassignedData?.uC_OpenOrderForPeriod ?? 0),
      callCountForTheDay: datatableData.reduce((sum, r) => sum + (r.callCountForTheDay ?? 0), 0),
      callCountMonthTillDate: datatableData.reduce((sum, r) => sum + (r.callCountMonthTillDate ?? 0), 0),
      totalAssignedCustomers: datatableData.reduce((sum, r) => sum + (r.totalAssignedCustomers ?? 0), 0),
      isTotal: true,
    };

    const mergedData: ExtendedCEPerformanceReportResponseItem[] = [
      ...datatableData,
      ...(unassignedRow ? [unassignedRow] : []),
      totalRow,
    ];

    const onRowClick = (rowData: ExtendedCEPerformanceReportResponseItem) => {
      if (rowData.isUnassigned) setPopupVisible(true);
    };

    // ✅ Handle pivot cell click
    const handlePivotCellClick = async (ceKey: string, row: CECallStatusPivotResponseItem) => {
      const salesExecutiveIdMatch = ceKey.match(/\((\d+)\)$/);
      if (!salesExecutiveIdMatch) return;
      
      const salesExecutiveId = Number(salesExecutiveIdMatch[1]);
      const salesExecutiveName = ceKey.replace(/\(\d+\)$/, "").trim();
      const callStatusId = row.callStatusId;
      const callStatusName = row.callStatusName;

      setSelectedPivotExecutive({ salesExecutiveId, salesExecutiveName });
      setSelectedPivotStatus({ callStatusId, callStatusName });

      await fetchPivotPopupData(salesExecutiveId, callStatusId, callStatusName, 1, pivotPageSize, pivotSortField, pivotSortOrder);
    };

    const fetchPivotPopupData = async (
      salesExecutiveId: number,
      callStatusId: number,
      callStatusName: string,
      page: number = pivotPageNumber,
      size: number = pivotPageSize,
      sortFieldParam: string = pivotSortField,
      sortOrderParam: 1 | -1 | 0 = pivotSortOrder
    ) => {
      if (!monthYear) return;

      setLoadingPivotPopup(true);

      const [year, month] = monthYear.split("-").map(Number);
      const startDate = new Date(year, month - 1, 1);
      const endDate =
        month === new Date().getMonth() + 1
          ? new Date()
          : new Date(year, month, 0);

    const formatDate = (date: Date): string => {
    return date.toLocaleDateString("en-CA"); 
  };


  const requestPayload = {
    salesExecutiveId,
    callStatusId,
    startDate: formatDate(startDate),
    endDate: formatDate(endDate),
    sortColumn: sortFieldParam,
    sortDirection: sortOrderParam === 1 ? "asc" : "desc",
    pageNumber: page,
    pageSize: size,
  };


      try {
        const response = await getCustomerCallStatusSummary(requestPayload);
        setPivotPopupData(response.data);
        setPivotTotalRecords(response.totalCount);
        const allDynamicKeys: string[] = [];

        response.data.forEach((item: any) => {
          if (item.dynamicColumns) {
            Object.keys(item.dynamicColumns).forEach((key) => {
              if (!allDynamicKeys.includes(key)) {
                allDynamicKeys.push(key);
              }
            });
          }
        });

        const sortedDynamicKeys = allDynamicKeys.sort((a, b) => {
          const getDateFromKey = (key: string) => {
            const parts = key.split('_')[0].split('-');
            const monthName = parts[0];
            const year = parseInt(parts[1]);
            
            const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", 
                          "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
            const monthIndex = months.findIndex(m => m === monthName);
            
            return new Date(year, monthIndex);
          }; 
          return getDateFromKey(b).getTime() - getDateFromKey(a).getTime();
        });

        setPivotDynamicColumns(sortedDynamicKeys);
        const dynamicColumnObjects = sortedDynamicKeys.map(field => ({
          field,
          header: field,
          isDynamic: true
        }));

        const allColumns = [...pivotBaseColumns, ...dynamicColumnObjects];
        setPivotAllAvailableColumns(allColumns);
        setPivotSelectedColumns(allColumns);
        setPivotPopupVisible(true);
      } catch (err) {
        console.error(err);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to fetch customer details",
          life: 3000,
        });
      } finally {
        setLoadingPivotPopup(false);
      }
    };

    const paginatorTemplate = {
      layout: "RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink NextPageLink LastPageLink",
      RowsPerPageDropdown: (options: any) => {
        const dropdownOptions = [
          { label: 10, value: 10 },
          { label: 20, value: 20 },
          { label: 50, value: 50 },
          { label: 100, value: 100 },
        ];

        return (
          <div className="flex align-items-center">
            <span className="text-sm text-600 mr-2">Show</span>
            <Dropdown value={options.value} options={dropdownOptions} onChange={options.onChange} className="w-4rem" />
            <span className="text-sm text-600 ml-2">entries</span>
          </div>
        );
      },
      CurrentPageReport: (options: any) => (
        <span className="mx-3 text-sm text-600">
          Showing {options.first} to {options.last} of {pivotTotalRecords} entries
        </span>
      ),
    };

    const isOrderReceivedStatus = selectedPivotStatus?.callStatusName.toLowerCase().includes("order received");

    return (
      <Suspense fallback={<AppLoading />}>
        <Toast ref={toast} />
        <div className="layout-content mt-3 mb-3">
          <ReportPageHeader
            icon="pi-chart-line"
            title="Customer Executive Performance Report"
            subtitle="Review executive targets, invoicing progress, call counts, and call status trends"
            filterLabel="Filter Performance Report"
          >
            <div className="grid">
              <div className="col-12 md:col-4">
                <Calendar
                  value={monthYear ? new Date(monthYear + "-01") : null}
                  onChange={(e) => {
                    if (e.value) {
                      const selectedDate = e.value as Date;
                      const year = selectedDate.getFullYear();
                      const month = (selectedDate.getMonth() + 1).toString().padStart(2, "0");
                      setMonthYear(`${year}-${month}`);
                    }
                  }}
                  view="month"
                  dateFormat="M, yy"
                  maxDate={new Date()}
                  showIcon
                  className="custom-calendar w-full"
                  readOnlyInput
                />
              </div>
              <div className="col-6 md:col-2">
                <Button
                  label="Filter"
                  icon="pi pi-filter"
                  severity="success"
                  className="w-full"
                  onClick={handleRefresh}
                />
              </div>
              <div className="col-6 md:col-2">
                <Button
                  label="Reset"
                  icon="pi pi-refresh"
                  className="w-full"
                  text
                  raised
                  onClick={handleResetFilters}
                />
              </div>
            </div>
          </ReportPageHeader>

          {/* Summary Cards */}
          <div className="grid mb-4">
            <div className="col-12 md:col-4">
              <Card className="shadow-md border-round-2xl text-center p-3 surface-50">
                <h5>Reached Customers</h5>
                <p className="text-2xl font-bold text-primary mt-1">
                  {cardData ? formatNumber(cardData.total_CustomersCalledWithinRange) : "--"}
                </p>
              </Card>
            </div>
            <div className="col-12 md:col-4">
              <Card className="shadow-md border-round-2xl text-center p-3 surface-50">
                <h5>Total Order Value</h5>
                <p className="text-2xl font-bold text-green-600 mt-1">
                  {cardData ? formatRupees(cardData.total_TotalTaxableAmount) : "--"}
                </p>
              </Card>
            </div>
            <div className="col-12 md:col-4">
              <Card className="border-round-2xl text-center p-3 surface-50">
                <h5>Reached Customers Order Value</h5>
                <p className="text-2xl font-bold text-green-500 mt-1">
                  {cardData ? formatRupees(cardData.total_TotalTaxableAmountFromReachedCustomers) : "--"}
                </p>
              </Card>
            </div>
          </div>

          {/* Main CE Performance Table */}
          <DataTable
            value={mergedData}
            headerColumnGroup={headerGroup}
            loading={loading}
            paginator
            rows={10}
            rowsPerPageOptions={[5, 10, 25, 50]}
            emptyMessage="No data found"
            responsiveLayout="scroll"
            className="p-datatable-sm shadow-md border-round-lg"
            showGridlines
            rowClassName={(row) =>
              row.isUnassigned ? "bg-blue-50 font-bold cursor-pointer" : row.isTotal ? "bg-gray-200 font-bold" : ""
            }
            onRowClick={(e) => onRowClick(e.data as ExtendedCEPerformanceReportResponseItem)}
          >
            <Column field="customerServiceExecutive" header="Customer Service Executive" sortable />
            <Column
              field="targetForTheMonth"
              body={(row) => (row.isUnassigned ? "-" : formatRupees(row.targetForTheMonth))}
              style={{ textAlign: "right" }}
              header="Target"
              sortable
            />
            <Column
              field="invoicedForTheDay"
              body={(row) => formatRupees(row.invoicedForTheDay)}
              style={{ textAlign: "right" }}
              header={`Invoices (${formatDate(today)})`}
              sortable
            />
            <Column
              field="invoicedTillDate"
              body={(row) => formatRupees(row.invoicedTillDate)}
              style={{ textAlign: "right" }}
              header="Cumulative Monthly Invoices"
              sortable
            />
            <Column
              field="balanceTargetToAchieve"
              body={(row) => (row.isUnassigned ? "-" : formatNumber(row.balanceTargetToAchieve))}
              style={{ textAlign: "right" }}
              header="Balance Target"
              sortable
            />
            <Column
              field="achievedPercent"
              body={(row) =>
                row.isUnassigned || row.isTotal ? (
                  "-"
                ) : (
                  <span
                    className={`px-2 py-1 text-xs font-bold border-round ${
                      row.achievedPercent >= 80
                        ? "bg-green-100 text-green-700"
                        : row.achievedPercent >= 50
                        ? "bg-orange-100 text-orange-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {formatNumber(row.achievedPercent)}%
                  </span>
                )
              }
              style={{ textAlign: "right" }}
              header="Target Achieved (%)"
              sortable
            />
            <Column
              field="openOrderAsOnDate"
              body={(row) => formatRupees(row.openOrderAsOnDate)}
              style={{ textAlign: "right" }}
              header={`Open Order (${formatDate(today)})`}
              sortable
            />
            <Column
              field="callCountForTheDay"
              body={(row) => (row.isUnassigned ? "-" : formatNumber(row.callCountForTheDay))}
              style={{ textAlign: "center" }}
              header={`Calls (${formatDate(today)})`}
              sortable
            />
            <Column
              field="callCountMonthTillDate"
              body={(row) => (row.isUnassigned ? "-" : formatNumber(row.callCountMonthTillDate))}
              style={{ textAlign: "center" }}
              header="Cumulative Monthly Calls"
              sortable
            />
            <Column
              field="totalAssignedCustomers"
              body={(row) => (row.isUnassigned ? "-" : formatNumber(row.totalAssignedCustomers))}
              style={{ textAlign: "center" }}
              header="Total Assigned"
              sortable
            />
          </DataTable>

          {/* Pivot Table */}
          <div className="mt-6">
            <h5 className="mb-3">Customer Executive Call Status Report</h5>
            <DataTable
              value={pivotData}
              loading={loadingPivot}  
              paginator
              rows={20}
              emptyMessage="No data found"
              responsiveLayout="scroll"
              className="p-datatable-sm shadow-md border-round-lg"
              showGridlines
              tableStyle={{ minWidth: "50rem" }}
              rowClassName={(rowData) => {
                const status = rowData.callStatusName.toLowerCase();
                if (status.includes("order received") || status.includes("will order shortly")) {
                  return "bg-green-50";
                }
                if (status.includes("don't call this number") || status.includes("dnd")) {
                  return "bg-red-50";
                }
                if (status.includes("not interested") || status.includes("price high")) {
                  return "bg-orange-50";
                }
                return "bg-purple-50";
              }}
            >
              <Column 
                field="callStatusName" 
                header="Call Status" 
                footer="Total:" 
                style={{ fontWeight: "bold" }} 
                sortable 
              />
              
              {ceColumns.map((ce) => {
                const cleanHeader = ce.replace(/\(\d+\)$/, "");
                return (
                  <Column
                    key={ce}
                    header={cleanHeader}
                    body={(row) => (
                      <span
                        onClick={() => handlePivotCellClick(ce, row)}
                        title={`Click to see customers for ${cleanHeader}`}
                        style={{ color: "#2563EB", cursor: "pointer", fontWeight: "bold" }}
                      >
                        {formatNumber(row.ceCounts[ce])}
                      </span>
                    )}
                    footer={formatNumber(totals[ce])}
                    style={{ textAlign: "center", fontWeight: "500" }}
                    sortable
                  />
                );
              })}
              
              {/* Total Column */}
              <Column
                field="rowTotal"
                header="Total"
                body={(row) => formatNumber(row.rowTotal || 0)}
                footer={formatNumber(pivotData.reduce((sum, row) => sum + (row.rowTotal || 0), 0))}
                style={{ textAlign: "center", fontWeight: "bold" }}
                sortable
              />
            </DataTable>
          </div>

          {/* Unassigned Customers Popup */}
          {unassignedData && (
            <UnassignedCustomerPopup
              visible={popupVisible}
              onHide={() => setPopupVisible(false)}
              year={Number(monthYear.split("-")[0])}
              month={Number(monthYear.split("-")[1])}
            />
          )}

          {/* Pivot Table Popup with Column Selection */}
          <Dialog
            header={
              <div className="flex justify-content-between align-items-center w-full">
                <span>Customers - {selectedPivotStatus?.callStatusName || ""} ({selectedPivotExecutive?.salesExecutiveName || ""})</span>
                <MultiSelect
                  value={pivotSelectedColumns}
                  options={pivotAllAvailableColumns}
                  optionLabel="header"
                  onChange={(e) => setPivotSelectedColumns(e.value)}
                  display="chip"
                  placeholder="Select Columns"
                  className="w-20rem"
                  maxSelectedLabels={3}
                  selectedItemsLabel="{0} columns selected"
                />
              </div>
            }
            visible={pivotPopupVisible}
            style={{ width: "90vw", maxWidth: "1200px" }}
            onHide={() => setPivotPopupVisible(false)}
            modal
            className="customer-dialog"
          >
            {isOrderReceivedStatus && (
              <div className="mb-3 p-3 border-round-md bg-blue-50 text-blue-900 border-1 border-blue-200">
                <i className="pi pi-info-circle mr-2"></i>
                The last order from the customer was marked as received by the executive.
              </div>
            )}

            <DataTable
              value={pivotPopupData}
              paginator
              rows={pivotPageSize}
              totalRecords={pivotTotalRecords}
              lazy
              loading={loadingPivotPopup}
              first={(pivotPageNumber - 1) * pivotPageSize}
              onPage={(e) => {
                const newPage = (e.page ?? 0) + 1;
                const newSize = e.rows ?? pivotPageSize;
                setPivotPageNumber(newPage);
                setPivotPageSize(newSize);
                if (selectedPivotStatus && selectedPivotExecutive) {
                  fetchPivotPopupData(
                    selectedPivotExecutive.salesExecutiveId,
                    selectedPivotStatus.callStatusId,
                    selectedPivotStatus.callStatusName,
                    newPage,
                    newSize,
                    pivotSortField,
                    pivotSortOrder
                  );
                }
              }}
              onSort={(e) => {
                setPivotSortField(e.sortField || "customerName");
                setPivotSortOrder((e.sortOrder as 1 | -1 | 0) ?? 0);
                if (selectedPivotStatus && selectedPivotExecutive) {
                  fetchPivotPopupData(
                    selectedPivotExecutive.salesExecutiveId,
                    selectedPivotStatus.callStatusId,
                    selectedPivotStatus.callStatusName,
                    pivotPageNumber,
                    pivotPageSize,
                    e.sortField || "customerName",
                    (e.sortOrder as 1 | -1 | 0) ?? 0
                  );
                }
              }}
              sortField={pivotSortField}
              sortOrder={pivotSortOrder}
              responsiveLayout="scroll"
              paginatorTemplate={paginatorTemplate}
              className="p-datatable-sm striped-rows"
              rowClassName={() => "cursor-pointer"}
              emptyMessage="No customers found."
              showGridlines
            >
              {pivotSelectedColumns.map((col) => (
                <Column
                  key={col.field}
                  field={col.field}
                  header={col.header}
                  body={(rowData) => pivotColumnBodyTemplate(rowData, col)}
                  
                  style={col.field === "taxableAmount" ? { textAlign: "right" } : {}}
                />
              ))}
            </DataTable>
          </Dialog>
        </div>
         {/* Last Orders Popup */}
<Dialog
  header="Last 5 Orders"
  visible={lastOrdersPopupVisible}
  style={{ width: "60vw", maxWidth: "700px", borderRadius: "8px" }}
  onHide={() => setLastOrdersPopupVisible(false)}
  modal
  className="last-orders-dialog"
  contentStyle={{ padding: "1rem", backgroundColor: "#f9f9f9" }}
  headerStyle={{ backgroundColor: "#007f3d", color: "#fff", fontWeight: 600, fontSize: "1.1rem", borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}
>
  <DataTable
    value={lastOrdersData}
    loading={loadingLastOrders}
    paginator
    rows={5}
    responsiveLayout="scroll"
    emptyMessage="No orders found."
    className="p-datatable-sm p-datatable-striped"
    showGridlines
    style={{ borderRadius: "6px", overflow: "hidden" }}
  >
    <Column field="orderID" header="Order ID" sortable style={{ width: "20%" }} />
    <Column field="createdDate" header="Order Date" body={(row) => formatDateTime(row.createdDate)} sortable style={{ width: "30%" }} />
    <Column field="taxableAmount" header="Taxable Amount" body={(row) => formatRupees(row.taxableAmount)} style={{ textAlign: "right", width: "25%" }} sortable />
    <Column field="createdByName" header="Created By" sortable style={{ width: "25%" }} />
  </DataTable>
</Dialog>

        {loading && <AppLoading />}
      </Suspense>
    );
  };

  export default CECardsMonthYearDropdown;
